import { redirect } from '@tanstack/react-router'
import { useSessionStore, selectAudience } from '@/lib/session.store'
import { hydrateSessionFromSharedCookie } from '@/lib/session-hydrate'
import { shellBounceUrl, shellOwnsAuth } from '@/lib/shell-bounce'
import type { User } from '@/modules/shared/types'

/**
 * Set while bouncing to the shell, so a bounce can happen at most once per tab.
 *
 * The shell holds a valid session (its own refresh succeeded, which is why it
 * sent the visitor here) while this app cannot see one. Without a latch that is
 * an infinite redirect: shell → app → shell → app. One bounce is the useful
 * case — someone genuinely signed out. A second means the two sides disagree,
 * and stopping to say so beats spinning.
 */
const BOUNCE_FLAG = 'cc:shell-bounced'

function readBounceFlag(): boolean {
  try {
    return window.sessionStorage.getItem(BOUNCE_FLAG) === '1'
  } catch {
    // Storage can throw outright (private mode, blocked cookies). Treating that
    // as "already bounced" keeps the failure a dead end rather than a loop.
    return true
  }
}

function setBounceFlag(): void {
  try {
    window.sessionStorage.setItem(BOUNCE_FLAG, '1')
  } catch {
    /* nothing to do; the read side fails closed */
  }
}

export function clearShellBounceLatch(): void {
  clearBounceFlag()
}

function clearBounceFlag(): void {
  try {
    window.sessionStorage.removeItem(BOUNCE_FLAG)
  } catch {
    /* ignore */
  }
}

/**
 * Guard for authenticated (staff) routes. Redirects when there's no
 * staff-audience session. Now that staff and client share one `session.store`
 * (auth-unification U2), a present token isn't enough — it could be a client
 * session — so this also checks the decoded `audience`. Returns the current
 * token and user so the loader can pass them through context.
 *
 * Async because arriving from the shell means having no token yet: the access
 * token is per-origin and only the refresh cookie crosses subdomains. See
 * `session-hydrate.ts`.
 */
export async function requireAuth(): Promise<{ token: string; user: User }> {
  if (!hasStaffSession()) {
    await hydrateSessionFromSharedCookie()
  }

  const { token, user } = useSessionStore.getState()
  if (!token || selectAudience({ token }) !== 'staff') {
    // Sign-in belongs to the shell on the apex — an app with its own login form
    // is a second way in that knows nothing about apps. Carry the current URL so
    // the shell returns the visitor here rather than to the app's home.
    if (shellOwnsAuth() && typeof window !== 'undefined' && !readBounceFlag()) {
      setBounceFlag()
      window.location.replace(shellBounceUrl(window.location.href))
      // `replace` does not halt execution; the throw stops the loader from
      // continuing to render a route the visitor is not authorised for.
      throw redirect({ to: '/login' })
    }
    throw redirect({ to: '/login' })
  }

  clearBounceFlag()
  return { token, user }
}

function hasStaffSession(): boolean {
  const { token } = useSessionStore.getState()
  return !!token && selectAudience({ token }) === 'staff'
}

/**
 * Reverse guard for public routes (login, register). Sends authenticated
 * staff users to the root, which then routes them to their workspace.
 */
export function requireGuest(): void {
  const { token } = useSessionStore.getState()
  if (token && selectAudience({ token }) === 'staff') {
    throw redirect({ to: '/' })
  }
}
